/* eslint-disable react/no-unused-prop-types */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { arrayOf, bool, instanceOf, number, oneOf, oneOfType, shape, string } from 'prop-types';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { isEqual } from 'lodash';

import Filter from '../../page-framework/filter/FilterDecorator';
import { filterPropTypes, filterPropDefaults } from '../../page-framework/filter/FilterPropTypes';
import TimeRangeFilterStateServiceFactory, {
    getPeriodTimeRange,
    title as filterTitle,
} from './TimeRangeFilterStateService';
import {
    START_LABEL,
    END_LABEL,
    CUSTOM_LABEL,
    CUSTOM_ID,
    DEFAULT_OPTIONS,
    IDS,
    MIN_DATE_TIME_VALUE,
    getPeriod,
} from './TimeRangeFilterConstants';
import messageDescriptor from '../../lingui/LinguiPropTypes';
import DateTime from './DateTime';
import ValidationError from '../../form/ValidationError';
import { floorToMinute } from '../../../utilities/DateTimeUtilities';

export const filterId = 'TimeRange';

function TimeRangeFilter(props) {
    const { state, filterActions, options } = props;
    const resolvedOptions = useMemo(
        () => ({
            ...DEFAULT_OPTIONS,
            ...options,
        }),
        [options]
    );
    const [periodId, setPeriodId] = useState(state?.id);
    const [customStart, setCustomStart] = useState(state?.start);
    const [customEnd, setCustomEnd] = useState(state?.end);
    const [startValid, setStartValid] = useState(true);
    const [endValid, setEndValid] = useState(true);
    const [validity, setValidity] = useState({
        range: true,
        minRange: true,
        maxRange: true,
        minStart: true,
        start: true,
        end: true,
    });

    useEffect(() => {
        const startMs = customStart.getTime();
        const endMs = customEnd.getTime();
        const rangeMs = endMs - startMs;
        const newValidity = {
            ...validity,
            range: rangeMs > 0,
            minRange: rangeMs >= resolvedOptions.minRangeMs,
            maxRange: rangeMs <= resolvedOptions.maxRangeMs,
            minStart: startMs >= MIN_DATE_TIME_VALUE,
        };

        if (isEqual(validity, newValidity)) return;

        setValidity(newValidity);
    }, [validity, customStart, customEnd, resolvedOptions]);

    useEffect(() => {
        filterActions.setValid(
            validity.start &&
                validity.end &&
                validity.range &&
                validity.minRange &&
                validity.maxRange &&
                validity.minStart
        );
        setStartValid(validity.range && validity.minRange && validity.maxRange && validity.minStart);
        setEndValid(validity.range && validity.minRange && validity.maxRange);
    }, [validity, filterActions]);

    function handlePeriodChanged(event) {
        const newPeriodId = event.target.value;
        let newState = {
            id: newPeriodId,
        };

        setPeriodId(newPeriodId);

        if (newPeriodId === CUSTOM_ID) {
            newState = {
                ...newState,
                start: customStart,
                end: customEnd,
            };
        } else {
            const period = getPeriod(newPeriodId);
            newState = {
                ...newState,
                ...getPeriodTimeRange(period),
            };

            setCustomStart(newState.start);
            setCustomEnd(newState.end);
        }

        filterActions.setState(newState);
    }

    const handleStartChanged = useCallback(
        (newStart) => {
            const start = floorToMinute(newStart);
            setCustomStart(start);
            filterActions.setState({
                ...state,
                start,
            });
        },
        [filterActions, state]
    );

    const handleStartValidChanged = useCallback(
        (valid) => {
            if (validity.start === valid) return;
            setValidity({
                ...validity,
                start: valid,
            });
        },
        [validity]
    );

    const handleEndChanged = useCallback(
        (newEnd) => {
            const end = floorToMinute(newEnd);
            setCustomEnd(end);
            filterActions.setState({
                ...state,
                end,
            });
        },
        [filterActions, state]
    );

    const handleEndValidChanged = useCallback(
        (valid) => {
            if (validity.end === valid) return;
            setValidity({
                ...validity,
                end: valid,
            });
        },
        [validity]
    );

    function renderCustomFields() {
        if (!resolvedOptions.enableCustom) return null;
        return (
            <>
                <DateTime
                    error={!startValid}
                    label={i18n._(START_LABEL)}
                    value={customStart}
                    disabled={periodId !== CUSTOM_ID}
                    onChange={handleStartChanged}
                    onValidChange={handleStartValidChanged}
                />
                <DateTime
                    error={!endValid}
                    label={i18n._(END_LABEL)}
                    value={customEnd}
                    disabled={periodId !== CUSTOM_ID}
                    onChange={handleEndChanged}
                    onValidChange={handleEndValidChanged}
                />
                <ValidationError invalid={!validity.range} message={i18n._(t`The start must be before the end`)} />
                <ValidationError
                    invalid={!validity.minRange && validity.range}
                    message={i18n._(t`The start and end cannot be less than one minute apart`)}
                />
                <ValidationError
                    invalid={!validity.maxRange}
                    message={i18n._(t`The start and end cannot be more than one year apart`)}
                />
                <ValidationError
                    invalid={!validity.minStart}
                    message={i18n._(t`The start cannot be before the Unix epoch`)}
                />
            </>
        );
    }

    return (
        <>
            <RadioGroup name="timeRange" value={periodId} onChange={handlePeriodChanged}>
                {resolvedOptions.timeRanges.map((timeRange) => {
                    return (
                        <FormControlLabel
                            value={timeRange.id}
                            key={timeRange.id}
                            control={<Radio />}
                            label={i18n._(timeRange.name)}
                        />
                    );
                }) || null}
                {resolvedOptions.enableCustom && (
                    <FormControlLabel value={CUSTOM_ID} control={<Radio />} label={i18n._(CUSTOM_LABEL)} />
                )}
            </RadioGroup>
            {renderCustomFields()}
        </>
    );
}

TimeRangeFilter.propTypes = {
    ...filterPropTypes,
    state: shape({
        id: oneOf(IDS),
        start: instanceOf(Date),
        end: instanceOf(Date),
    }),
    options: shape({
        enableCustom: bool,
        alignToGranularity: bool,
        timeRanges: arrayOf(
            shape({
                id: string,
                name: oneOfType([string, messageDescriptor]),
                durationInMinutes: number,
            })
        ),
        minRangeMs: number,
        maxRangeMs: number,
    }),
};

TimeRangeFilter.defaultProps = {
    ...filterPropDefaults,
    id: filterId,
    title: filterTitle,
    autoRefresh: true,
    options: DEFAULT_OPTIONS,
    state: undefined,
};

export default Filter({
    filterId,
    filterStateServiceFactory: TimeRangeFilterStateServiceFactory,
})(TimeRangeFilter);
